<script>
  import Cropper from 'cropperjs';

  import { clearInputFile } from '@/scripts/helpers/utils';

  export default {
    props: {
      label: {
        type: String,
        default: 'Выберите файл',
      },
      maxFileSize: {
        type: Number,
        default: 5 * 1024 * 1024, // 5mb
      },
      ratio: {
        type: Number,
        default: 16 / 9,
      },
      imageWidth: {
        type: Number,
        default: 640,
      },
      imageHeight: {
        type: Number,
        default: 360,
      },
      mimeTypes: {
        type: Array,
        default: () => [
          'image/jpeg',
          'image/png',
        ],
      },
      thumb: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        cropper: null,
        canvasImage: '',
        thumbImage: '',
        blob: null,
        filename: '',
        mimetype: '',
        removed: false,
        state: 'empty',
      };
    },
    created() {
      if (this.thumb) {
        this.thumbImage = this.thumb;
        this.state = 'cropped';
      }
    },
    methods: {
      initCropper() {
        this.cropper = new Cropper(this.$refs.canvas, {
          aspectRatio: this.ratio,
          viewMode: 2,
        });
      },
      destroyCropper() {
        if (this.cropper) {
          this.cropper.destroy();
        }
      },
      onFileChange(e) {
        const files = e.target.files || e.dataTransfer.files;
        const file = files[0];

        if (!file) return;

        if (file.size > this.maxFileSize) {
          return;
        }

        if (!this.mimeTypes.includes(file.type)) {
          return;
        }

        this.filename = file.name;
        this.mimetype = file.type;

        const reader = new window.FileReader();

        reader.addEventListener('load', () => {
          clearInputFile(e.target);
          this.canvasImage = reader.result;
        });

        reader.readAsDataURL(file);
        this.state = 'cropping';
      },
      save() {
        const canvas = this.cropper.getCroppedCanvas({
          width: this.imageWidth,
          height: this.imageHeight,
          fillColor: '#fff',
          imageSmoothingEnabled: false,
          imageSmoothingQuality: 'high',
        });

        canvas.toBlob((blob) => {
          this.blob = blob;
          this.$emit('add', {
            blob: this.blob,
            filename: this.filename,
            mimetype: this.mimetype,
          });
          this.thumbImage = canvas.toDataURL('image/jpeg', 0.75);
          this.destroyCropper();
          this.state = 'cropped';
        });
      },
      cancel() {
        this.destroyCropper();
        this.clear();

        if (!this.removed && this.thumb) {
          this.thumbImage = this.thumb;
          this.state = 'cropped';
          return;
        }

        this.state = 'empty';
      },
      remove() {
        this.clear();
        this.$emit('remove');
        if (this.thumb) {
          this.removed = true;
        }
        this.state = 'empty';
      },
      clear() {
        this.blob = null;
        this.filename = '';
        this.mimetype = '';
        this.canvasImage = '';
        this.thumbImage = '';
      },
    },
  };
</script>

<template>
  <div class="image-cropper">
    <template v-if="state === 'empty'">
      <label class="image-cropper__holder btn btn_sub btn_medium">
        {{ label }}
        <input
          :accept="mimeTypes.join(',')"
          tabindex="-1"
          type="file"
          class="sr-only"
          @change="onFileChange"
        >
      </label>
    </template>
    <template v-if="state === 'cropping'">
      <div class="image-cropper__media">
        <div class="image-cropper__image">
          <img
            ref="canvas"
            :src="canvasImage"
            @load="initCropper"
          >
        </div>
      </div>
      <div class="image-cropper__advice">
        Выберите область для обрезки фотографии.
      </div>
      <div class="image-cropper__footer">
        <div class="grid">
          <div class="col">
            <button
              type="button"
              class="btn btn_sub btn_small"
              @click="save"
            >
              Сохранить
            </button>
          </div>
          <div class="col">
            <button
              type="button"
              class="btn btn_dark btn_small"
              @click="cancel"
            >
              Отменить
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-if="state === 'cropped'">
      <div class="image-cropper__thumb">
        <img :src="thumbImage">
      </div>
      <div class="image-cropper__thumb-footer">
        <div class="grid">
          <div class="col">
            <label class="link link_dashed link_alt">
              Загрузить другое фото
              <input
                :accept="mimeTypes.join(',')"
                tabindex="-1"
                type="file"
                class="sr-only"
                @change="onFileChange"
              >
            </label>
          </div>
          <div class="col">
            <button
              type="button"
              class="link link_dashed"
              @click="remove"
            >
              Удалить
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" type="text/scss" scoped>
  .image-cropper {
    width: 100%;
    max-width: 480px;
    overflow: hidden;

    &__media {
      display: block;
      position: relative;
      width: 100%;
      height: 0;
      padding-bottom: 56.25%;
    }

    &__image {
      @include coverer;
    }

    &__thumb {
      @include img-block;
      max-width: 180px;
      width: 100%;
    }

    &__thumb-footer {
      @include font-size($font-size-small);
      margin-top: 5px;
    }

    &__advice {
      @include font-size($font-size-small);
      margin-top: 10px;
      color: $color-gray;
    }

    &__footer {
      margin-top: 10px;
    }
  }
</style>
